// src/components/provider/NotificationDashboard.jsx
import React, { useState } from 'react';
import { 
  Bell, 
  Filter, 
  Search, 
  Check, 
  Trash2, 
  Eye, 
  Clock,
  AlertCircle,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDistanceToNow, format } from 'date-fns';

export const NotificationDashboard = () => {
  const { 
    notifications, 
    stats, 
    loading, 
    markAsRead, 
    markAsResponded,
    markAllAsRead, 
    deleteNotification,
    getNotificationsByCategory,
    getUnreadNotifications,
    getActionRequiredNotifications
  } = useNotifications();

  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const filteredNotifications = notifications.filter(notification => {
    // Apply filter
    let matchesFilter = true;
    switch (filter) {
      case 'unread':
        matchesFilter = !notification.read;
        break;
      case 'action_required':
        matchesFilter = notification.actionRequired && !notification.responded;
        break;
      case 'accommodation':
        matchesFilter = notification.category === 'accommodation_request';
        break;
      case 'appointment':
        matchesFilter = notification.category === 'appointment_request';
        break;
      case 'rental':
        matchesFilter = notification.category === 'rental_request';
        break;
      case 'venue':
        matchesFilter = notification.category === 'venue_request';
        break;
    }

    // Apply search
    const matchesSearch = !searchTerm || 
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications.length === 0) return;
    
    try {
      await Promise.all(
        selectedNotifications.map(id => markAsRead(id))
      );
      setSelectedNotifications([]);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) return;
    
    if (window.confirm(`Delete ${selectedNotifications.length} notifications?`)) {
      try {
        await Promise.all(
          selectedNotifications.map(id => deleteNotification(id))
        );
        setSelectedNotifications([]);
      } catch (error) {
        console.error('Error deleting notifications:', error);
      }
    }
  };

  const renderNotificationDetails = (notification) => {
    const { requestData } = notification;
    if (!requestData) return null;

    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
        <div className="grid grid-cols-2 gap-4">
          {requestData.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span>{requestData.location}</span>
            </div>
          )}
          
          {requestData.dates && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span>{requestData.dates}</span>
            </div>
          )}
          
          {requestData.guests && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span>{requestData.guests}</span>
            </div>
          )}
          
          {requestData.budget && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span>{requestData.budget}</span>
            </div>
          )}
        </div>
        
        {requestData.specialRequirements && (
          <div className="mt-2">
            <span className="font-medium">Special Requirements:</span>
            <p className="text-gray-600">{requestData.specialRequirements}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Notifications</h1>
          <p className="text-gray-600">
            Manage your booking requests and provider notifications
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Bell className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
              </div>
              <Eye className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Action Required</p>
                <p className="text-2xl font-bold text-orange-600">{stats.unresponded}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-400" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-green-600">
                  {notifications.filter(n => 
                    new Date(n.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  ).length}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-400" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All', count: notifications.length },
                { key: 'unread', label: 'Unread', count: stats.unread },
                { key: 'action_required', label: 'Action Required', count: stats.unresponded },
                { key: 'accommodation', label: 'Accommodation', count: stats.byCategory.accommodation_request || 0 },
                { key: 'appointment', label: 'Appointments', count: stats.byCategory.appointment_request || 0 },
                { key: 'rental', label: 'Rentals', count: stats.byCategory.rental_request || 0 },
                { key: 'venue', label: 'Venues', count: stats.byCategory.venue_request || 0 }
              ].map(filterOption => (
                <button
                  key={filterOption.key}
                  onClick={() => setFilter(filterOption.key)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    filter === filterOption.key
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterOption.label} {filterOption.count > 0 && `(${filterOption.count})`}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedNotifications.length > 0 && (
            <div className="mt-4 flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-blue-700">
                {selectedNotifications.length} notifications selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkMarkAsRead}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                >
                  <Check className="h-3 w-3" />
                  Mark as Read
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
              <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
              <p className="text-gray-600">
                {filter === 'all' 
                  ? "You don't have any notifications yet."
                  : `No ${filter.replace('_', ' ')} notifications found.`
                }
              </p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`bg-white p-6 rounded-lg shadow-sm border transition-all hover:shadow-md ${
                  !notification.read ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />

                    {/* Notification Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          notification.priority === 'high' ? 'bg-red-100 text-red-700' :
                          notification.priority === 'low' ? 'bg-gray-100 text-gray-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {notification.priority}
                        </span>
                        
                        <span className="text-xs text-gray-500">
                          {notification.category.replace('_', ' ')}
                        </span>
                        
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                        </span>

                        {notification.actionRequired && !notification.responded && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            Action Required
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {notification.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-3">
                        {notification.message}
                      </p>

                      {/* Request Details */}
                      {renderNotificationDetails(notification)}

                      {/* Actions */}
                      {notification.actions && notification.actions.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {notification.actions.map((action, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                if (action.url) {
                                  window.open(action.url, '_blank');
                                }
                                if (action.type === 'respond') {
                                  markAsResponded(notification.id, { action: action.action });
                                }
                              }}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                action.type === 'respond' 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : action.type === 'view'
                                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {action.label}
                              {action.url && <ExternalLink className="h-3 w-3" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notification Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="p-2 hover:bg-gray-100 rounded text-gray-500"
                        title="Mark as read"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="p-2 hover:bg-red-100 rounded text-red-500"
                      title="Delete notification"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Quick Actions */}
        {stats.unread > 0 && (
          <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="flex gap-4">
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                Mark all as read
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};